import React from 'react';
import styled from 'styled-components';

import WeatherBoard from 'components/WeatherBoard';
import WallboardContext from 'contexts/WallboardContext';
import { SignOutButton } from 'screens/WallBoard/WallboardButtons';
import tokenVideoSrcMp4 from 'screens/WallBoard/tokenVideoMp4.mp4';
import tokenVideoSrcWebm from 'screens/WallBoard/tokenVideoWebm.webm';

import { PlaylistItem } from './buildPlaylist';
import { STALE_AFTER_MS } from './constants';
import FrameView from './FrameView';
import {
  FrameNotUpdatedBadge,
  FullScreenMessage,
  StaleBanner,
} from './PlayerMessages';
import { ManifestState } from './useWallboardManifest';

const Screen = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: var(--color-bg-primary);
`;

// Plays on a loop so TVs don't fall asleep or show their screensaver.
const KeepAwakeVideo = () => (
  <video
    width="2px"
    height="2px"
    autoPlay
    muted
    loop
    style={{ position: 'absolute', top: 0, right: 0 }}
  >
    <source src={tokenVideoSrcWebm} type="video/webm" />
    <source src={tokenVideoSrcMp4} type="video/mp4" />
  </video>
);

const PlayingManifest = ({
  manifest,
  item,
  previousUrl,
  playKey,
  viewport,
  now,
}: {
  manifest: WallboardScreenshots.ManifestResponse;
  item: PlaylistItem | undefined;
  previousUrl?: string;
  playKey: string;
  viewport: { width: number; height: number };
  now: number;
}) => {
  const slide = item ? manifest.slides[item.slideIndex] : undefined;
  const frame = slide?.frames[item?.frameIndex ?? 0];
  const isStale = now - Date.parse(manifest.generatedAt) > STALE_AFTER_MS;

  if (!slide) {
    return (
      <FullScreenMessage
        title="This wallboard has no slides"
        body="Add slides to the wallboard in FleetOps to show them here."
      />
    );
  }

  return (
    <>
      {slide.kind === 'weather' && (
        <WallboardContext.Provider
          value={{
            isWallboard: true,
            weatherMapZoom: manifest.weatherMapZoom,
            dashboardZoom: 1,
          }}
        >
          <div style={{ width: '100%', height: '100%' }}>
            <WeatherBoard />
          </div>
        </WallboardContext.Provider>
      )}
      {slide.kind === 'image' && frame && (
        <FrameView
          frame={frame}
          previousUrl={previousUrl}
          frameSize={manifest.frameSize}
          viewport={viewport}
          durationMs={item?.durationMs ?? manifest.intervalTimeSeconds * 1000}
          playKey={playKey}
        />
      )}
      {slide.kind === 'image' && frame && frame.status !== 'ok' && (
        <FrameNotUpdatedBadge generatedAt={frame.generatedAt} />
      )}
      {isStale && <StaleBanner generatedAt={manifest.generatedAt} />}
    </>
  );
};

const WallboardPlayer = ({
  manifestState,
  item,
  previousUrl,
  playKey = '0',
  viewport,
  now,
  isSignOutVisible,
}: {
  manifestState: ManifestState;
  item: PlaylistItem | undefined;
  previousUrl?: string;
  playKey?: string;
  viewport: { width: number; height: number };
  now: number;
  isSignOutVisible: boolean;
}) => (
  <Screen data-testid="wallboard-player">
    <KeepAwakeVideo />
    {(isSignOutVisible || manifestState.status === 'removed') && (
      <SignOutButton />
    )}
    {manifestState.status === 'loading' && (
      <FullScreenMessage title="Loading wallboard" isLoading />
    )}
    {manifestState.status === 'preparing' && (
      <FullScreenMessage
        title="Preparing wallboard…"
        body="The slides are being prepared. This usually takes a few minutes."
        isLoading
      />
    )}
    {manifestState.status === 'unavailable' && (
      <FullScreenMessage
        title="Can't reach FleetOps"
        body="Check this screen's internet connection. Retrying every minute."
        isLoading
      />
    )}
    {manifestState.status === 'removed' && (
      <FullScreenMessage
        title="This wallboard has been removed"
        body="Sign out, then set up this screen with a new access code."
      />
    )}
    {manifestState.status === 'ready' && (
      <PlayingManifest
        manifest={manifestState.manifest}
        item={item}
        previousUrl={previousUrl}
        playKey={playKey}
        viewport={viewport}
        now={now}
      />
    )}
  </Screen>
);

export default WallboardPlayer;
